import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { clsx } from 'clsx';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'size'> {
  /** Visual size variant */
  size?: 'sm' | 'md' | 'lg';
  /** Error message — triggers error styling when truthy */
  error?: string;
  /** Icon rendered inside the input on the left */
  icon?: ReactNode;
  /** Icon rendered inside the input on the right */
  iconRight?: ReactNode;
}

const SIZE_MAP = {
  sm: { input: 'h-8 text-2xs px-2.5', iconLeft: 'pl-7', iconRight: 'pr-7', iconSize: '[&>svg]:h-3.5 [&>svg]:w-3.5' },
  md: { input: 'h-9 text-sm px-3', iconLeft: 'pl-9', iconRight: 'pr-9', iconSize: '[&>svg]:h-4 [&>svg]:w-4' },
  lg: { input: 'h-11 text-sm px-3.5', iconLeft: 'pl-10', iconRight: 'pr-10', iconSize: '[&>svg]:h-5 [&>svg]:w-5' },
};

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ className, size = 'md', error, icon, iconRight, disabled, ...props }, ref) => {
    const s = SIZE_MAP[size];

    return (
      <div className="w-full">
        <div className="relative">
          {icon && (
            <span
              className={clsx(
                'pointer-events-none absolute inset-y-0 left-0 flex items-center pl-2.5 text-ink-dim',
                s.iconSize,
              )}
            >
              {icon}
            </span>
          )}
          <input
            ref={ref}
            disabled={disabled}
            aria-invalid={error ? true : undefined}
            className={clsx(
              'w-full rounded-lg border bg-surface-1 text-ink placeholder:text-ink-dim/50',
              'transition-colors duration-150',
              'focus:outline-hidden focus:border-primary focus:ring-2 focus:ring-primary/20',
              'disabled:cursor-not-allowed disabled:opacity-50',
              error && 'border-danger focus:border-danger focus:ring-danger/20',
              !error && 'border-border',
              s.input,
              icon && s.iconLeft,
              iconRight && s.iconRight,
              className,
            )}
            {...props}
          />
          {iconRight && (
            <span
              className={clsx(
                'pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2.5 text-ink-dim',
                s.iconSize,
              )}
            >
              {iconRight}
            </span>
          )}
        </div>
        {error && <p className="mt-1 text-2xs text-danger">{error}</p>}
      </div>
    );
  },
);

Input.displayName = 'Input';
